import { useNavigate } from "react-router-dom";
import { Clock, Tag } from "lucide-react";
import { useWelcomeDiscount } from "@/hooks/useWelcomeDiscount";
import { formatTimeRemaining, DISCOUNT_PERCENT, ORIGINAL_PRICE, DISCOUNTED_PRICE } from "@/lib/discountUtils";
import { getActiveSeasonalSale } from "@/lib/seasonalSaleConfig";
import { useTranslation } from "react-i18next";

export function WelcomeDiscountRibbon() {
  const { isActive, timeRemaining } = useWelcomeDiscount();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // A sitewide seasonal sale (Halloween/holiday) offers the same 20% to
  // everyone, so showing both ribbons at once would just be two banners
  // saying the same thing — the seasonal one wins while it's active.
  if (!isActive || !timeRemaining || getActiveSeasonalSale()) return null;

  const timeStr = formatTimeRemaining(timeRemaining);

  return (
    <div
      className="w-full py-2 px-4 text-center cursor-pointer z-[60] relative"
      style={{
        background: '#F5F0E8',
        borderBottom: '2px solid #C81400',
      }}
      onClick={() => navigate("/dashboard")}
      role="banner"
      aria-label={t("welcomeDiscount.ribbon.ariaLabel", "Limited time welcome discount")}
    >
      <div className="container mx-auto flex items-center justify-center gap-2 flex-wrap">
        <Tag className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#C81400' }} />
        <span
          className="text-xs sm:text-sm font-medium"
          style={{ color: '#1a1a1a', fontFamily: 'var(--font-body)' }}
        >
          {t("welcomeDiscount.ribbon.message", {
            defaultValue: "Welcome! Get {{percent}}% off your first mystery — ${{discountedPrice}} instead of ${{originalPrice}}",
            percent: DISCOUNT_PERCENT,
            discountedPrice: DISCOUNTED_PRICE.toFixed(2),
            originalPrice: ORIGINAL_PRICE.toFixed(2),
          })}
        </span>
        <span className="hidden sm:inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full"
          style={{ backgroundColor: '#C81400', color: '#ffffff' }}
        >
          <Clock className="h-3 w-3" />
          {t("welcomeDiscount.ribbon.expires", {
            defaultValue: "Expires in {{time}}",
            time: timeStr,
          })}
        </span>
        <span className="sm:hidden inline-flex items-center gap-1 text-xs font-medium"
          style={{ color: '#C81400' }}
        >
          <Clock className="h-3 w-3" />
          {timeStr}
        </span>
      </div>
    </div>
  );
}
